import { Box, Typography, capitalize } from '@mui/material';
import Toast, { ToastProps, ToastType } from 'components/Common/Toast';
import { useHttp } from 'hooks/use-fetch';
import EditWaypointsButton from 'pages/Dashboard/EditWaypointsButton';
import WaypointsTrackingTable from 'pages/Dashboard/components/WaypointsTrackingTable';
import useWaypointSuggestions from 'pages/Dashboard/hooks/useWaypointSuggestions';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { GroupedWaypoints, WaypointTracking } from 'pages/Dashboard/types/waypoints.types';
import React from 'react';
import { useParams } from 'react-router-dom';
import Theme from 'theme';
import { Logger } from 'utils/logger';

interface WaypointTrackingSectionProps {
  patient: PatientObj | null;
  timeFrame: string;
  currentWaypoints: WaypointTracking[];
  previousWaypoints: WaypointTracking[];
  allWaypoints: GroupedWaypoints;
}

export default function WaypointTrackingSection({
  patient,
  timeFrame,
  currentWaypoints,
  previousWaypoints,
  allWaypoints,
}: WaypointTrackingSectionProps) {
  const { id: patientId } = useParams();
  const { providerId, isNewVersionEnabled } = useHttp();

  const [toastProps, setToastProps] = React.useState<ToastProps | null>(null);

  const onError = (message: string) => {
    Logger.error(message);
    setToastProps({
      message,
      open: true,
      type: ToastType.error,
    });
  };

  const {
    standardWaypoints,
    patientWaypoints,
    waypointSuggestions,
    revokeWaypointSuggestions,
    sendWaypointSuggestions,
    isFetchingPatientWaypoints,
    isFetchingWaypointSuggestions,
    refetchWaypointSuggestions,
    refetchPatientWaypoints,
  } = useWaypointSuggestions({ providerId, patientId, onError });

  const patientName = patient?.name?.firstName && patient?.name?.firstName?.length > 0
    ? capitalize(patient?.name?.firstName ?? '')
    : 'the patient';

  async function refetch() {
    await refetchWaypointSuggestions();
    await refetchPatientWaypoints();
  }

  const activitiesText = isNewVersionEnabled ? 'Activities' : 'Waypoints';
  const hasPatientWaypoints = patientWaypoints.length > 0;
  const hasSuggestions = waypointSuggestions.length > 0;

  return (
    <>
      <Box mt={6}>
        <Box display='flex' alignItems='flex-start' justifyContent='space-between'>
          <Typography variant='h5' mb={2} fontWeight={400} color={Theme.custom.colors.lightTextPrimary}>
            Self-report
          </Typography>
          {patient && (
            <EditWaypointsButton
              patient={patient}
              standardWaypoints={standardWaypoints}
              patientWaypoints={patientWaypoints}
              waypointSuggestions={waypointSuggestions}
              handleDeleteConfirmation={revokeWaypointSuggestions}
              handleSendSuggestions={sendWaypointSuggestions}
              refetch={refetch}
              isLoading={isFetchingPatientWaypoints || isFetchingWaypointSuggestions}
            />
          )}
        </Box>
        {hasPatientWaypoints ? (
          <Typography variant='body2' color='gray' mt={1} className='fs-mask'>
            {`
            ${patientName} selects the occurrence rate of each activity using a “less/typical/more” scale. Below are their most frequent selections.
          `}
          </Typography>
        ) : (
          <>
            {!hasSuggestions ? (
              <Typography variant='body2' color='gray' mt={1} className='fs-mask'>
                {`
            ${patientName} has not selected any ${activitiesText.toLowerCase()} to track.
          `}
              </Typography>
            ) : null}
            <Typography variant='body2' color='gray' mt={1}>
              {activitiesText}
              {' '}
              are behaviors that the patient can track to help discover correlations to
              their mood.
            </Typography>
          </>
        )}
      </Box>
      <Box mt={4} display='flex' justifyContent='center'>
        <Box width={750}>
          {hasPatientWaypoints || waypointSuggestions.length ? (
            <WaypointsTrackingTable
              timeFrame={timeFrame}
              currentWaypoints={currentWaypoints}
              previousWaypoints={previousWaypoints}
              allWaypoints={allWaypoints}
              waypointSuggestions={waypointSuggestions}
              patient={patient}
            />
          ) : null}
        </Box>
      </Box>
      {toastProps ? <Toast {...toastProps} onClose={() => setToastProps(null)} /> : null}
    </>
  );
}
